import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useMutation } from 'react-query';
import {
  Box, Typography, Alert, CircularProgress,
  Tooltip, Drawer, Divider, List, ListItem,
} from '@mui/material';
import { Button, Dialog, TextField } from 'Components/ui';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useAuthTenant } from 'store/auth';
import { createScannerAccount, getScannerAccountConnection } from 'services/scanner-service';

// ── Constants ─────────────────────────────────────────────────────────────────
const VO_AWS_ACCOUNT_ID = '216813563501';
const VO_AWS_ACCOUNT_ARN = `arn:aws:iam::${VO_AWS_ACCOUNT_ID}:root`;
const VO_CFN_TEMPLATE_URL = 'https://vofficer-public-assets.s3.sa-east-1.amazonaws.com/vo-cspm-scan-role.yml';

// Same "quick create" link shape the underlying scan service uses to onboard a scan role via CloudFormation —
// pre-fills the template URL and ExternalId so the customer only has to review and submit.
function buildCfnQuickCreateUrl(externalId) {
  const params = new URLSearchParams({
    templateURL: VO_CFN_TEMPLATE_URL,
    stackName: 'VirtualOfficer',
    param_ExternalId: externalId,
  });
  return `https://us-east-1.console.aws.amazon.com/cloudformation/home?region=us-east-1#/stacks/quickcreate?${params.toString()}`;
}

function generateExternalId() {
  return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
    (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16)
  );
}

// ── Provider catalogue (colors are design tokens, not translatable text) ──────
const PROVIDER_META = [
  { id: 'aws',        initials: 'AWS',  color: '#FF9900', textColor: 'var(--vo-white)', available: true },
  { id: 'gcp',        initials: 'GCP',  color: '#4285F4', textColor: 'var(--vo-white)', available: true },
  { id: 'azure',      initials: 'AZ',   color: '#0078D4', textColor: 'var(--vo-white)', available: true },
  { id: 'm365',       initials: 'M365', color: '#D83B01', textColor: 'var(--vo-white)', available: false },
  { id: 'mongodb',    initials: 'MDB',  color: '#00ED64', textColor: 'var(--vo-ink)', available: true },
  { id: 'kubernetes', initials: 'K8s',  color: '#326CE5', textColor: 'var(--vo-white)', available: false },
  { id: 'github',     initials: 'GH',   color: '#24292E', textColor: 'var(--vo-white)', available: false },
];

// ── Shared helpers ─────────────────────────────────────────────────────────────
const BTN = {
  borderRadius: '8px', padding: '8px 20px', height: '36px',
  textTransform: 'none', fontSize: '0.875rem', fontWeight: 500,
};

const T = {
  bg: 'var(--vo-mist)',
  border: 'var(--vo-line)',
  text: 'var(--vo-ink)',
  muted: 'var(--vo-slate)',
  dim: 'var(--vo-steel)',
};

function copy(text, copiedLabel) {
  navigator.clipboard.writeText(text);
  Dialog.fire({ toast: true, icon: 'success', title: copiedLabel, position: 'top-end', showConfirmButton: false, timer: 1200 });
}

const CopyBox = ({ value, copiedLabel }) => (
  <Box sx={{
    display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'center', gap: 1.5,
    background: 'var(--vo-mist)', border: `1px solid ${T.border}`, borderRadius: 1.5,
    p: '10px 14px', mb: 1.5,
  }}>
    <Typography sx={{ fontSize: 13, fontFamily: 'monospace', color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
      {value}
    </Typography>
    <Tooltip title={value}>
      <Button size="sm" variant="secondary" onClick={() => copy(value, copiedLabel)}
        sx={{ flexShrink: 0, minWidth: 36, height: 30, px: 1, borderRadius: '6px', borderColor: T.border }}>
        <ContentCopyIcon sx={{ fontSize: 15 }} />
      </Button>
    </Tooltip>
  </Box>
);

const Step = ({ heading, body }) => (
  <ListItem alignItems="flex-start" sx={{ px: 0, py: 1.5, display: 'block' }}>
    <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'var(--vo-ink)', mb: 0.5 }}>{heading}</Typography>
    <Typography sx={{ fontSize: 12.5, color: 'var(--vo-ink-2)', lineHeight: 1.6 }}>{body}</Typography>
  </ListItem>
);

const DocsDrawer = ({ open, onClose, doc, tipLabel, integrationGuideLabel }) => {
  if (!doc) return null;
  return (
    <Drawer anchor="right" open={open} onClose={onClose}
      PaperProps={{ sx: { width: 400, p: 0, display: 'flex', flexDirection: 'column' } }}>
      {/* Header */}
      <Box sx={{ px: 3, py: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid var(--vo-line)', flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 32, height: 32, borderRadius: 1, backgroundColor: doc.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Typography sx={{ fontSize: 10, fontWeight: 700, color: doc.textColor || 'var(--vo-white)', lineHeight: 1 }}>{doc.initials}</Typography>
          </Box>
          <Box>
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'var(--vo-ink)' }}>{doc.title}</Typography>
            <Typography sx={{ fontSize: 11, color: 'var(--vo-steel)' }}>{integrationGuideLabel}</Typography>
          </Box>
        </Box>
        <Button size="sm" variant="text" onClick={onClose} sx={{ minWidth: 32, p: 0.5, color: 'var(--vo-steel)' }}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </Button>
      </Box>

      {/* Steps */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2 }}>
        <List disablePadding>
          {doc.steps.map((s, i) => (
            <Box key={i}>
              <Step heading={s.heading} body={s.body} />
              {i < doc.steps.length - 1 && <Divider />}
            </Box>
          ))}
        </List>

        {doc.tip && (
          <Box sx={{ mt: 2, p: 2, borderRadius: 1.5, backgroundColor: 'var(--vo-warning-10)', border: '1px solid var(--vo-warning-15)' }}>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'var(--vo-warning)', mb: 0.5 }}>{tipLabel}</Typography>
            <Typography sx={{ fontSize: 12, color: 'var(--vo-ink-2)', lineHeight: 1.6 }}>{doc.tip}</Typography>
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

// ── Sidebar ───────────────────────────────────────────────────────────────────
const Sidebar = ({ currentStep, steps }) => {
  const sidebarIdx = currentStep === 0 ? 0 : 1;

  return (
    <Box sx={{
      width: 240, flexShrink: 0,
      backgroundColor: 'var(--vo-mist)',
      borderRight: `1px solid ${T.border}`,
      p: 3, display: 'flex', flexDirection: 'column',
    }}>
      {steps.map((s, i) => {
        const done   = i < sidebarIdx;
        const active = i === sidebarIdx;
        return (
          <Box key={s.label} sx={{
            display: 'flex', gap: 1.5, py: 2,
            borderBottom: i < steps.length - 1 ? `1px solid ${T.border}` : 'none',
          }}>
            <Box sx={{ mt: 0.3, flexShrink: 0, color: done ? 'var(--vo-success)' : active ? 'var(--color-brand)' : 'var(--vo-steel)' }}>
              {done
                ? <CheckCircleIcon sx={{ fontSize: 18 }} />
                : <RadioButtonUncheckedIcon sx={{ fontSize: 18 }} />}
            </Box>
            <Box>
              <Typography sx={{
                fontSize: 13, fontWeight: active ? 600 : 400,
                color: active ? T.text : done ? 'var(--vo-ink-2)' : T.dim,
                lineHeight: 1.3,
              }}>
                {s.label}
              </Typography>
              <Typography sx={{
                fontSize: 11, mt: 0.4, lineHeight: 1.4,
                color: T.muted,
                display: active ? 'block' : 'none',
              }}>
                {s.desc}
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
export const ScannerAccountNew = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('scanner');
  const selectedTenant = useSelector(useAuthTenant);

  // ── i18n-derived content (rebuilt each render; cheap, not perf-critical) ────
  const SIDEBAR_STEPS = [
    { label: t('scanner:accountNew.sidebar.step1Label'), desc: t('scanner:accountNew.sidebar.step1Desc') },
    { label: t('scanner:accountNew.sidebar.step2Label'), desc: t('scanner:accountNew.sidebar.step2Desc') },
    { label: t('scanner:accountNew.sidebar.step3Label'), desc: t('scanner:accountNew.sidebar.step3Desc') },
    { label: t('scanner:accountNew.sidebar.step4Label'), desc: t('scanner:accountNew.sidebar.step4Desc') },
  ];

  const PROVIDERS = PROVIDER_META.map(p => ({ ...p, label: t(`scanner:accountNew.providers.${p.id}`) }));

  const DOCS = {
    aws: { ...t('scanner:accountNew.docs.aws', { returnObjects: true }), color: '#FF9900', initials: 'AWS' },
    gcp: { ...t('scanner:accountNew.docs.gcp', { returnObjects: true }), color: '#4285F4', initials: 'GCP' },
    azure: { ...t('scanner:accountNew.docs.azure', { returnObjects: true }), color: '#0078D4', initials: 'AZ' },
    mongodb: { ...t('scanner:accountNew.docs.mongodb', { returnObjects: true }), color: '#00ED64', initials: 'MDB', textColor: 'var(--vo-ink)' },
  };

  // Wizard state
  const [step, setStep]                     = useState(0);  // 0 = select provider, 1+ = provider-specific
  const [provider, setProvider]             = useState(null);
  const [awsMethod, setAwsMethod]           = useState(null); // 'role' | 'creds'
  const [name, setName]                     = useState('');
  const [accountId, setAccountId]           = useState('');
  const [alias, setAlias]                   = useState('');
  const [roleArn, setRoleArn]               = useState('');
  const [externalId]                        = useState(generateExternalId);
  const [azureTenantId, setAzureTenantId]   = useState('');
  const [azureClientId, setAzureClientId]   = useState('');
  const [azureClientSecret, setAzureClientSecret] = useState('');
  const [gcpJson, setGcpJson]               = useState('');
  const [mongoOrgId, setMongoOrgId]           = useState('');
  const [mongoPublicKey, setMongoPublicKey]   = useState('');
  const [mongoPrivateKey, setMongoPrivateKey] = useState('');
  const [awsAccessKey, setAwsAccessKey]       = useState('');
  const [awsSecretKey, setAwsSecretKey]       = useState('');
  const [awsSessionToken, setAwsSessionToken] = useState('');
  const [showSecret, setShowSecret]           = useState(false);
  const [docsOpen, setDocsOpen]               = useState(false);

  // ── Connection validation state ──────────────────────────────────────────────
  const [validating, setValidating]           = useState(false);
  const [connStatus, setConnStatus]           = useState(null); // null | 'pending' | 'connected' | 'failed'
  const pollRef = useRef(null);

  const iamTrustPolicy = JSON.stringify({
    Version: '2012-10-17',
    Statement: [{
      Effect: 'Allow',
      Principal: { AWS: VO_AWS_ACCOUNT_ARN },
      Action: 'sts:AssumeRole',
      Condition: { StringEquals: { 'sts:ExternalId': externalId } },
    }],
  }, null, 2);

  // ── Stop polling on unmount ──────────────────────────────────────────────────
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const startConnectionPolling = (accountId, slug) => {
    setValidating(true);
    setConnStatus('pending');

    const POLL_INTERVAL = 4000;
    const TIMEOUT_MS = 60000;
    const startedAt = Date.now();

    pollRef.current = setInterval(async () => {
      try {
        const res = await getScannerAccountConnection(accountId, slug);
        const { status } = res.data ?? {};
        if (status === 'connected' || status === 'failed') {
          clearInterval(pollRef.current);
          setConnStatus(status);
        } else if (Date.now() - startedAt > TIMEOUT_MS) {
          clearInterval(pollRef.current);
          setConnStatus('failed');
        }
      } catch {
        // keep polling
      }
    }, POLL_INTERVAL);
  };

  const mutation = useMutation(
    (payload) => createScannerAccount(payload),
    {
      onSuccess: (res) => {
        const { validationError, _id } = res.data ?? {};
        if (validationError) {
          Dialog.fire({
            icon: 'warning',
            title: t('scanner:accountNew.dialogs.accountWarningTitle'),
            html: `<p>${validationError}</p>
              <p>${t('scanner:accountNew.dialogs.accountWarningCheck')}</p>
              <ul style="text-align:left">
                <li>${t('scanner:accountNew.dialogs.principalLabel')} <code>${VO_AWS_ACCOUNT_ARN}</code></li>
                <li>${t('scanner:accountNew.dialogs.conditionLabel')} <code>${externalId}</code></li>
              </ul>`,
          });
          navigate('/scanner/accounts');
        } else if (_id) {
          startConnectionPolling(_id, selectedTenant?.slug);
        } else {
          navigate('/scanner/accounts');
        }
      },
      onError: (err) =>
        Dialog.fire({
          title: t('scanner:accountNew.dialogs.genericErrorTitle'),
          text: err?.response?.data?.message || t('scanner:accountNew.dialogs.genericErrorText'),
          icon: 'error',
        }),
    }
  );

  const handleSubmit = () => {
    const effectiveName = name || alias || accountId || t('scanner:accountNew.effectiveNamePrefix') + (provider ?? '').toUpperCase();
    const payload = { name: effectiveName, provider, companyKey: selectedTenant.slug };
    if (provider === 'aws') {
      if (awsMethod === 'credentials') {
        payload.aws = { method: 'static', accountId, accessKeyId: awsAccessKey, secretAccessKey: awsSecretKey, ...(awsSessionToken ? { sessionToken: awsSessionToken } : {}) };
      } else {
        payload.aws = { roleArn, externalId };
      }
    } else if (provider === 'azure') {
      payload.azure = { tenantId: azureTenantId, clientId: azureClientId, clientSecret: azureClientSecret };
    } else if (provider === 'gcp') {
      payload.gcp = { serviceAccountJson: gcpJson };
    } else if (provider === 'mongodb') {
      payload.mongodb = { organizationId: mongoOrgId, publicApiKey: mongoPublicKey, privateApiKey: mongoPrivateKey };
    }
    mutation.mutate(payload);
  };

  // ── Next / Back ──────────────────────────────────────────────────────────
  const canGoNext = () => {
    if (step === 0) return !!provider;
    if (provider === 'aws') {
      if (step === 1) return !!accountId;   // Account ID entered
      if (step === 2) return !!awsMethod;   // Auth method chosen
      if (step === 3) {
        if (awsMethod === 'role') return !!roleArn;
        if (awsMethod === 'credentials') return !!awsAccessKey && !!awsSecretKey;
        return false;
      }
    }
    if (provider === 'azure') {
      if (step === 1) return !!azureTenantId && !!azureClientId && !!azureClientSecret;
    }
    if (provider === 'gcp') {
      if (step === 1) return !!gcpJson;
    }
    if (provider === 'mongodb') {
      if (step === 1) return !!mongoOrgId && !!mongoPublicKey && !!mongoPrivateKey;
    }
    return true;
  };

  const goNext = () => setStep(s => s + 1);
  const goBack = () => {
    if (step === 0) navigate('/scanner/accounts');
    else setStep(s => s - 1);
  };

  // Final step differs per provider
  const isFinalStep = () => {
    if (provider === 'aws')     return step === 3;
    if (provider === 'azure')   return step === 2;
    if (provider === 'gcp')     return step === 2;
    if (provider === 'mongodb') return step === 2;
    return false;
  };

  // ── Docs label per provider ───────────────────────────────────────────────
  const docsLabel = provider === 'aws' ? t('scanner:accountNew.docsLabel.aws')
    : provider === 'azure' ? t('scanner:accountNew.docsLabel.azure')
    : provider === 'gcp' ? t('scanner:accountNew.docsLabel.gcp')
    : provider === 'mongodb' ? t('scanner:accountNew.docsLabel.mongodb')
    : t('scanner:accountNew.docsLabel.default');

  // ── Connection validation screen ─────────────────────────────────────────
  if (validating) {
    const isPending   = connStatus === 'pending';
    const isConnected = connStatus === 'connected';
    const isFailed    = connStatus === 'failed';
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 120px)', backgroundColor: 'var(--vo-white)', overflow: 'hidden' }}>
        <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${T.border}`, flexShrink: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 16, color: T.text }}>{t('scanner:accountNew.validating.title')}</Typography>
        </Box>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3, p: 4 }}>
          {isPending && (
            <>
              <CircularProgress size={56} sx={{ color: 'var(--color-brand)' }} />
              <Typography sx={{ fontSize: 16, fontWeight: 600, color: T.text }}>{t('scanner:accountNew.validating.checkingCredentials')}</Typography>
              <Typography sx={{ fontSize: 13, color: T.muted, textAlign: 'center', maxWidth: 400 }}>
                {t('scanner:accountNew.validating.checkingBody')}
              </Typography>
            </>
          )}
          {isConnected && (
            <>
              <CheckCircleIcon sx={{ fontSize: 64, color: 'var(--vo-success)' }} />
              <Typography sx={{ fontSize: 18, fontWeight: 700, color: 'var(--vo-success)' }}>{t('scanner:accountNew.validating.connectedTitle')}</Typography>
              <Typography sx={{ fontSize: 13, color: T.muted, textAlign: 'center', maxWidth: 400 }}>
                {t('scanner:accountNew.validating.connectedBody')}
              </Typography>
              <Button variant="primary" onClick={() => navigate('/scanner/accounts')}
                sx={{ ...BTN, backgroundColor: 'var(--color-brand)', color: 'var(--vo-white) !important', '&:hover': { backgroundColor: 'var(--color-brand-strong)' } }}>
                {t('scanner:accountNew.validating.viewAccounts')}
              </Button>
            </>
          )}
          {isFailed && (
            <>
              <ErrorOutlineIcon sx={{ fontSize: 64, color: 'var(--vo-danger)' }} />
              <Typography sx={{ fontSize: 18, fontWeight: 700, color: 'var(--vo-danger)' }}>{t('scanner:accountNew.validating.failedTitle')}</Typography>
              <Typography sx={{ fontSize: 13, color: T.muted, textAlign: 'center', maxWidth: 440 }}>
                {t('scanner:accountNew.validating.failedBody')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button variant="outline" onClick={() => { setValidating(false); setConnStatus(null); }}
                  sx={{ ...BTN, borderColor: T.border, color: T.muted }}>
                  {t('scanner:accountNew.validating.retry')}
                </Button>
                <Button variant="danger" onClick={() => navigate('/scanner/accounts')}
                  sx={{ ...BTN, backgroundColor: 'var(--vo-danger)', color: 'var(--vo-white) !important', '&:hover': { backgroundColor: 'var(--vo-pink-600)' } }}>
                  {t('scanner:accountNew.validating.exitAnyway')}
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Box>
    );
  }

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column',
      height: 'calc(100vh - 120px)',
      backgroundColor: 'var(--vo-white)',
      overflow: 'hidden',
    }}>

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 16, color: T.text, whiteSpace: 'nowrap' }}>{t('scanner:accountNew.header')}</Typography>
        {provider && DOCS[provider] && (
          <Button
            variant="text"
            size="sm"
            startIcon={<MenuBookIcon sx={{ fontSize: 15 }} />}
            sx={{ fontSize: 12, textTransform: 'none', color: 'var(--color-brand)' }}
            onClick={() => setDocsOpen(true)}
          >
            {docsLabel}
          </Button>
        )}
      </Box>

      {/* ── Body ────────────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
        <Sidebar currentStep={step} steps={SIDEBAR_STEPS} />

        {/* Content area */}
        <Box sx={{ flex: 1, p: 3, display: 'flex', flexDirection: 'column', gap: 2, overflowY: 'auto' }}>

            {/* ── Step 0: Select provider ────────────────────────────────── */}
            {step === 0 && (
              <Box>
                <Typography sx={{ fontSize: 13, color: T.muted, mb: 2 }}>
                  {t('scanner:accountNew.selectProvider.subtitle')}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {PROVIDERS.map(p => (
                    <Box
                      key={p.id}
                      onClick={() => p.available && setProvider(p.id)}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 2,
                        p: '14px 16px', borderRadius: 1.5, cursor: p.available ? 'pointer' : 'not-allowed',
                        border: `1px solid ${provider === p.id ? 'var(--color-brand)' : T.border}`,
                        backgroundColor: provider === p.id ? 'var(--vo-info-8)' : p.available ? 'var(--vo-white)' : 'var(--vo-mist)',
                        opacity: p.available ? 1 : 0.45,
                        '&:hover': p.available ? { borderColor: 'var(--color-brand)', backgroundColor: 'var(--vo-info-8)' } : {},
                        transition: 'all 0.15s',
                      }}
                    >
                      {/* Provider icon badge */}
                      <Box sx={{
                        width: 38, height: 38, borderRadius: 1.5, flexShrink: 0,
                        backgroundColor: p.color, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: p.textColor, letterSpacing: 0.3 }}>
                          {p.initials}
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: 14, fontWeight: 500, color: T.text, flex: 1 }}>
                        {p.label}
                      </Typography>
                      {!p.available && (
                        <Box sx={{ px: 1, py: 0.3, borderRadius: 1, backgroundColor: 'var(--vo-mist)', border: `1px solid ${T.border}` }}>
                          <Typography sx={{ fontSize: 10, fontWeight: 600, color: T.muted }}>{t('scanner:accountNew.comingSoon')}</Typography>
                        </Box>
                      )}
                      {provider === p.id && (
                        <CheckCircleIcon sx={{ fontSize: 18, color: 'var(--color-brand)', flexShrink: 0 }} />
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* ── Step 1 (AWS): Account details ─────────────────────────── */}
            {step === 1 && provider === 'aws' && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                  <Box sx={{ width: 32, height: 32, borderRadius: 1, backgroundColor: '#FF9900', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'var(--vo-white)' }}>AWS</Typography>
                  </Box>
                  <Typography sx={{ fontSize: 15, fontWeight: 600, color: T.text }}>{t('scanner:accountNew.providers.aws')}</Typography>
                </Box>

                <Typography sx={{ fontSize: 12, color: T.muted, mb: 2 }}>
                  {t('scanner:accountNew.aws.accountDetails.subtitle')}
                </Typography>

                <Box
                  onClick={() => {/* single account - already the only option */}}
                  sx={{
                    p: 2, border: `1px solid var(--color-brand)`, borderRadius: 1.5, mb: 1.5,
                    backgroundColor: 'var(--vo-info-8)', cursor: 'default',
                    display: 'flex', alignItems: 'center', gap: 2,
                  }}
                >
                  <Box sx={{ width: 18, height: 18, borderRadius: '50%', border: '2px solid var(--color-brand)', backgroundColor: 'var(--color-brand)', flexShrink: 0 }} />
                  <Typography sx={{ fontSize: 13, fontWeight: 500, color: T.text }}>{t('scanner:accountNew.aws.accountDetails.singleAccount')}</Typography>
                </Box>

                <Box sx={{
                  p: 2, border: `1px solid ${T.border}`, borderRadius: 1.5,
                  backgroundColor: 'var(--vo-mist)', opacity: 0.5,
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ width: 18, height: 18, borderRadius: '50%', border: `2px solid ${T.border}`, flexShrink: 0 }} />
                    <Typography sx={{ fontSize: 13, color: T.muted }}>{t('scanner:accountNew.aws.accountDetails.multiAccount')}</Typography>
                  </Box>
                  <Box sx={{ px: 1, py: 0.3, borderRadius: 1, backgroundColor: 'var(--vo-capri-50)', border: '1px solid var(--vo-capri-100)' }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 600, color: 'var(--color-brand-strong)' }}>{t('scanner:accountNew.comingSoon')}</Typography>
                  </Box>
                </Box>

                <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    label={t('scanner:accountNew.aws.accountDetails.accountIdLabel')}
                    placeholder={t('scanner:accountNew.aws.accountDetails.accountIdPlaceholder')}
                    value={accountId}
                    onChange={e => setAccountId(e.target.value.replace(/\D/g, '').slice(0, 12))}
                    fullWidth
                    inputProps={{ maxLength: 12, inputMode: 'numeric' }}
                  />
                  <TextField
                    label={t('scanner:accountNew.accountNameLabel')}
                    placeholder={t('scanner:accountNew.aws.accountNamePlaceholderFallback')}
                    value={alias}
                    onChange={e => setAlias(e.target.value)}
                    fullWidth
                    helperText={t('scanner:accountNew.accountNameHelper')}
                  />
                </Box>
              </Box>
            )}

            {/* ── Step 2 (AWS): Auth method ─────────────────────────────── */}
            {step === 2 && provider === 'aws' && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                  <Box sx={{ width: 32, height: 32, borderRadius: 1, backgroundColor: '#FF9900', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'var(--vo-white)' }}>AWS</Typography>
                  </Box>
                  <Typography sx={{ fontSize: 15, fontWeight: 600, color: T.text }}>{t('scanner:accountNew.providers.aws')}</Typography>
                </Box>

                <Typography sx={{ fontSize: 12, color: T.muted, mb: 2 }}>{t('scanner:accountNew.aws.authMethod.usingRole')}</Typography>

                <Box
                  onClick={() => setAwsMethod('role')}
                  sx={{
                    p: 2, mb: 1.5, borderRadius: 1.5, cursor: 'pointer',
                    border: `1px solid ${awsMethod === 'role' ? 'var(--color-brand)' : T.border}`,
                    backgroundColor: awsMethod === 'role' ? 'var(--vo-info-8)' : 'var(--vo-white)',
                    display: 'flex', alignItems: 'center', gap: 2,
                    '&:hover': { borderColor: 'var(--color-brand)' },
                  }}
                >
                  <Box sx={{ width: 18, height: 18, borderRadius: '50%', flexShrink: 0, border: `2px solid ${awsMethod === 'role' ? 'var(--color-brand)' : T.border}`, backgroundColor: awsMethod === 'role' ? 'var(--color-brand)' : 'transparent' }} />
                  <Typography sx={{ fontSize: 13, fontWeight: awsMethod === 'role' ? 600 : 400, color: T.text }}>{t('scanner:accountNew.aws.authMethod.connectRole')}</Typography>
                </Box>

                <Typography sx={{ fontSize: 12, color: T.muted, mb: 1 }}>{t('scanner:accountNew.aws.authMethod.usingCredentials')}</Typography>

                <Box
                  onClick={() => setAwsMethod('credentials')}
                  sx={{
                    p: 2, borderRadius: 1.5, cursor: 'pointer',
                    border: `1px solid ${awsMethod === 'credentials' ? 'var(--color-brand)' : T.border}`,
                    backgroundColor: awsMethod === 'credentials' ? 'var(--vo-info-8)' : 'var(--vo-white)',
                    display: 'flex', alignItems: 'center', gap: 2,
                    '&:hover': { borderColor: 'var(--color-brand)' },
                  }}
                >
                  <Box sx={{ width: 18, height: 18, borderRadius: '50%', flexShrink: 0, border: `2px solid ${awsMethod === 'credentials' ? 'var(--color-brand)' : T.border}`, backgroundColor: awsMethod === 'credentials' ? 'var(--color-brand)' : 'transparent' }} />
                  <Typography sx={{ fontSize: 13, fontWeight: awsMethod === 'credentials' ? 600 : 400, color: T.text }}>{t('scanner:accountNew.aws.authMethod.connectCredentials')}</Typography>
                </Box>
              </Box>
            )}

            {/* ── Step 3 (AWS + IAM Role): Trust policy + Role ARN ────────── */}
            {step === 3 && provider === 'aws' && awsMethod === 'role' && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <Box sx={{ width: 32, height: 32, borderRadius: 1, backgroundColor: '#FF9900', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'var(--vo-white)' }}>AWS</Typography>
                  </Box>
                  <Typography sx={{ fontSize: 15, fontWeight: 600, color: T.text }}>{t('scanner:accountNew.aws.authMethod.connectRole')}</Typography>
                </Box>

                <Alert severity="info" sx={{ mb: 2, fontSize: 12 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.5 }}>
                    {t('scanner:accountNew.aws.roleSetup.recommendedTitle')}
                  </Typography>
                  <Typography sx={{ fontSize: 12, mb: 1.5, lineHeight: 1.5 }}>
                    {t('scanner:accountNew.aws.roleSetup.recommendedBody')}
                  </Typography>
                  <Button
                    variant="contained"
                    size="sm"
                    startIcon={<OpenInNewIcon sx={{ fontSize: 15 }} />}
                    onClick={() => window.open(buildCfnQuickCreateUrl(externalId), '_blank', 'noopener,noreferrer')}
                    sx={{ ...BTN, backgroundColor: '#FF9900', color: 'var(--vo-white) !important', '&:hover': { filter: 'brightness(0.92)' } }}
                  >
                    {t('scanner:accountNew.aws.roleSetup.createRoleBtn')}
                  </Button>
                </Alert>

                <Alert severity="info" sx={{ mb: 2, fontSize: 12 }}>
                  {t('scanner:accountNew.aws.roleSetup.manualInfo')}
                </Alert>

                <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.text, mb: 0.5 }}>
                  {t('scanner:accountNew.aws.roleSetup.step1Title')}
                </Typography>
                <CopyBox value={VO_AWS_ACCOUNT_ARN} copiedLabel={t('scanner:accountNew.dialogs.copied')} />

                <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.text, mb: 0.5 }}>
                  {t('scanner:accountNew.aws.roleSetup.step2Title')}
                </Typography>
                <Box sx={{ mb: 1.5 }}>
                  <Box sx={{ background: 'var(--vo-mist)', border: `1px solid ${T.border}`, borderRadius: 1.5, p: 1.5 }}>
                    <pre style={{ fontSize: 11, margin: 0, color: T.text, whiteSpace: 'pre-wrap' }}>{iamTrustPolicy}</pre>
                  </Box>
                  <Button size="sm" variant="text" onClick={() => copy(iamTrustPolicy, t('scanner:accountNew.dialogs.copied'))}
                    startIcon={<ContentCopyIcon sx={{ fontSize: 13 }} />}
                    sx={{ mt: 0.5, height: 28, px: 1, borderRadius: '6px', fontSize: 11, textTransform: 'none', color: T.dim }}>
                    {t('scanner:accountNew.aws.roleSetup.copyPolicyBtn')}
                  </Button>
                </Box>

                <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.text, mb: 0.5 }}>
                  {t('scanner:accountNew.aws.roleSetup.step3Title')}
                </Typography>
                <CopyBox value={externalId} copiedLabel={t('scanner:accountNew.dialogs.copied')} />

                <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.text, mb: 0.5, mt: 1 }}>
                  <Trans
                    i18nKey="scanner:accountNew.aws.roleSetup.step4Title"
                    components={[<strong key="0" />, <strong key="1" />]}
                    values={{
                      policy1: t('scanner:accountNew.awsPolicyNames.securityAudit'),
                      policy2: t('scanner:accountNew.awsPolicyNames.viewOnlyAccess'),
                    }}
                  />
                </Typography>

                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    label={t('scanner:accountNew.aws.roleSetup.roleArnLabel')}
                    placeholder={t('scanner:accountNew.aws.roleSetup.roleArnPlaceholder')}
                    value={roleArn}
                    onChange={e => setRoleArn(e.target.value)}
                    fullWidth
                    helperText={t('scanner:accountNew.aws.roleSetup.roleArnHelper')}
                  />
                </Box>
              </Box>
            )}

            {/* ── Step 3 (AWS + Credentials): Access Key form ─────────────── */}
            {step === 3 && provider === 'aws' && awsMethod === 'credentials' && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <Box sx={{ width: 32, height: 32, borderRadius: 1, backgroundColor: '#FF9900', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'var(--vo-white)' }}>AWS</Typography>
                  </Box>
                  <Typography sx={{ fontSize: 15, fontWeight: 600, color: T.text }}>{t('scanner:accountNew.aws.authMethod.connectCredentials')}</Typography>
                </Box>

                <Alert severity="warning" sx={{ mb: 2.5, fontSize: 12 }}>
                  <Trans
                    i18nKey="scanner:accountNew.aws.credentials.warning"
                    components={[<strong key="0" />]}
                    values={{ policy: t('scanner:accountNew.awsPolicyNames.securityAudit') }}
                  />
                </Alert>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    label={t('scanner:accountNew.aws.credentials.accessKeyLabel')}
                    placeholder={t('scanner:accountNew.aws.credentials.accessKeyPlaceholder')}
                    value={awsAccessKey}
                    onChange={e => setAwsAccessKey(e.target.value.trim())}
                    fullWidth
                    helperText={t('scanner:accountNew.aws.credentials.accessKeyHelper')}
                    inputProps={{ spellCheck: false }}
                  />
                  <TextField
                    label={t('scanner:accountNew.aws.credentials.secretKeyLabel')}
                    placeholder={t('scanner:accountNew.aws.credentials.secretKeyPlaceholder')}
                    value={awsSecretKey}
                    onChange={e => setAwsSecretKey(e.target.value.trim())}
                    fullWidth
                    type={showSecret ? 'text' : 'password'}
                    helperText={
                      <Box component="span" sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>{t('scanner:accountNew.aws.credentials.secretKeyHelperLabel')}</span>
                        <Box component="span" sx={{ cursor: 'pointer', color: 'var(--color-brand)', userSelect: 'none' }} onClick={() => setShowSecret(v => !v)}>
                          {showSecret ? t('scanner:accountNew.aws.credentials.hideBtn') : t('scanner:accountNew.aws.credentials.showBtn')}
                        </Box>
                      </Box>
                    }
                    inputProps={{ spellCheck: false }}
                  />
                  <TextField
                    label={t('scanner:accountNew.aws.credentials.sessionTokenLabel')}
                    placeholder={t('scanner:accountNew.aws.credentials.sessionTokenPlaceholder')}
                    value={awsSessionToken}
                    onChange={e => setAwsSessionToken(e.target.value.trim())}
                    fullWidth
                    helperText={t('scanner:accountNew.aws.credentials.sessionTokenHelper')}
                    inputProps={{ spellCheck: false }}
                  />
                </Box>
              </Box>
            )}

            {/* ── Step 1 (Azure): Credentials ──────────────────────────── */}
            {step === 1 && provider === 'azure' && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                  <Box sx={{ width: 32, height: 32, borderRadius: 1, backgroundColor: '#0078D4', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'var(--vo-white)' }}>AZ</Typography>
                  </Box>
                  <Typography sx={{ fontSize: 15, fontWeight: 600, color: T.text }}>{t('scanner:accountNew.providers.azure')}</Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField label={t('scanner:accountNew.azure.credentials.tenantIdLabel')} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" value={azureTenantId} onChange={e => setAzureTenantId(e.target.value)} fullWidth />
                  <TextField label={t('scanner:accountNew.azure.credentials.clientIdLabel')} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" value={azureClientId} onChange={e => setAzureClientId(e.target.value)} fullWidth />
                  <TextField label={t('scanner:accountNew.azure.credentials.clientSecretLabel')} type="password" value={azureClientSecret} onChange={e => setAzureClientSecret(e.target.value)} fullWidth />
                  <TextField label={t('scanner:accountNew.accountNameLabel')} placeholder={t('scanner:accountNew.azure.accountNamePlaceholderFallback')} value={name} onChange={e => setName(e.target.value)} fullWidth helperText={t('scanner:accountNew.accountNameHelper')} />
                </Box>
              </Box>
            )}

            {/* ── Step 2 (Azure): Confirm ───────────────────────────────── */}
            {step === 2 && provider === 'azure' && (
              <Box>
                <Alert severity="info" sx={{ mb: 2 }}>
                  {t('scanner:accountNew.azure.confirm.info')}
                </Alert>
                <Typography sx={{ fontSize: 13, color: T.muted }}>
                  {t('scanner:accountNew.azure.confirm.tenantIdSummary')} <strong>{azureTenantId}</strong><br />
                  {t('scanner:accountNew.azure.confirm.clientIdSummary')} <strong>{azureClientId}</strong>
                </Typography>
              </Box>
            )}

            {/* ── Step 1 (GCP): Service Account ────────────────────────── */}
            {step === 1 && provider === 'gcp' && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                  <Box sx={{ width: 32, height: 32, borderRadius: 1, backgroundColor: '#4285F4', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'var(--vo-white)' }}>GCP</Typography>
                  </Box>
                  <Typography sx={{ fontSize: 15, fontWeight: 600, color: T.text }}>{t('scanner:accountNew.providers.gcp')}</Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField label={t('scanner:accountNew.gcp.serviceAccountLabel')} multiline rows={8} value={gcpJson} onChange={e => setGcpJson(e.target.value)} fullWidth placeholder='{"type": "service_account", "project_id": "...", ...}' />
                  <TextField label={t('scanner:accountNew.accountNameLabel')} placeholder={t('scanner:accountNew.gcp.accountNamePlaceholderFallback')} value={name} onChange={e => setName(e.target.value)} fullWidth />
                </Box>
              </Box>
            )}

            {/* ── Step 2 (GCP): Confirm ────────────────────────────────── */}
            {step === 2 && provider === 'gcp' && (
              <Box>
                <Alert severity="info" sx={{ mb: 2 }}>
                  {t('scanner:accountNew.azure.confirm.info')}
                </Alert>
              </Box>
            )}

            {/* ── Step 1 (MongoDB): Credentials ────────────────────────── */}
            {step === 1 && provider === 'mongodb' && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                  <Box sx={{ width: 32, height: 32, borderRadius: 1, backgroundColor: '#00ED64', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'var(--vo-ink)' }}>MDB</Typography>
                  </Box>
                  <Typography sx={{ fontSize: 15, fontWeight: 600, color: T.text }}>{t('scanner:accountNew.providers.mongodb')}</Typography>
                </Box>
                <Alert severity="info" sx={{ mb: 2.5, fontSize: 12 }}>
                  <Trans
                    i18nKey="scanner:accountNew.mongodb.apiKeyInfo"
                    components={[<strong key="0" />]}
                    values={{ policy: t('scanner:accountNew.mongodb.policyName') }}
                  />
                </Alert>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    label={t('scanner:accountNew.mongodb.orgIdLabel')}
                    placeholder={t('scanner:accountNew.mongodb.orgIdPlaceholder')}
                    value={mongoOrgId}
                    onChange={e => setMongoOrgId(e.target.value)}
                    fullWidth
                    helperText={t('scanner:accountNew.mongodb.orgIdHelper')}
                  />
                  <TextField
                    label={t('scanner:accountNew.mongodb.publicKeyLabel')}
                    placeholder={t('scanner:accountNew.mongodb.publicKeyPlaceholder')}
                    value={mongoPublicKey}
                    onChange={e => setMongoPublicKey(e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label={t('scanner:accountNew.mongodb.privateKeyLabel')}
                    type="password"
                    placeholder="••••••••••••••••••••••••••••••••"
                    value={mongoPrivateKey}
                    onChange={e => setMongoPrivateKey(e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label={t('scanner:accountNew.accountNameLabel')}
                    placeholder={t('scanner:accountNew.mongodb.accountNamePlaceholderFallback')}
                    value={name}
                    onChange={e => setName(e.target.value)}
                    fullWidth
                    helperText={t('scanner:accountNew.accountNameHelper')}
                  />
                </Box>
              </Box>
            )}

            {/* ── Step 2 (MongoDB): Confirm ────────────────────────────── */}
            {step === 2 && provider === 'mongodb' && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <Box sx={{ width: 32, height: 32, borderRadius: 1, backgroundColor: '#00ED64', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'var(--vo-ink)' }}>MDB</Typography>
                  </Box>
                  <Typography sx={{ fontSize: 15, fontWeight: 600, color: T.text }}>{t('scanner:accountNew.providers.mongodb')}</Typography>
                </Box>
                <Alert severity="info" sx={{ mb: 2 }}>
                  {t('scanner:accountNew.azure.confirm.info')}
                </Alert>
                <Typography sx={{ fontSize: 13, color: T.muted }}>
                  {t('scanner:accountNew.mongodb.confirm.orgIdSummary')} <strong style={{ color: T.text }}>{mongoOrgId}</strong><br />
                  {t('scanner:accountNew.mongodb.confirm.publicKeySummary')} <strong style={{ color: T.text }}>{mongoPublicKey}</strong>
                </Typography>
              </Box>
            )}

        </Box>
      </Box>

      {/* ── Footer ──────────────────────────────────────────────────────── */}
      <Box sx={{
        px: 3, py: 2,
        borderTop: `1px solid ${T.border}`,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        backgroundColor: 'var(--vo-mist)',
        flexShrink: 0,
      }}>
        <Button
          variant="outline"
          onClick={goBack}
          sx={{ height: '44px', padding: '0 20px' }}
        >
          {step === 0 ? t('common:buttons.cancel') : t('scanner:accounts.backButton')}
        </Button>

        <Box sx={{ flexShrink: 0 }}>
          {!isFinalStep() ? (
            <Button
              variant="primary"
              disabled={!canGoNext()}
              onClick={goNext}
              sx={{
                ...BTN,
                backgroundColor: 'var(--color-brand)',
                color: 'var(--vo-white) !important',
                '&:hover': { backgroundColor: 'var(--color-brand-strong)' },
                '&.Mui-disabled': { backgroundColor: 'var(--vo-capri-200)', color: 'var(--vo-white) !important' },
              }}
            >
              {t('scanner:accountNew.footer.next')}
            </Button>
          ) : (
            <Button
              variant="primary"
              disabled={!canGoNext() || mutation.isLoading}
              onClick={handleSubmit}
              startIcon={mutation.isLoading ? <CircularProgress size={14} color="inherit" /> : null}
              sx={{
                ...BTN,
                backgroundColor: 'var(--color-brand)',
                color: 'var(--vo-white) !important',
                '&:hover': { backgroundColor: 'var(--color-brand-strong)' },
                '&.Mui-disabled': { backgroundColor: 'var(--vo-capri-200)', color: 'var(--vo-white) !important' },
              }}
            >
              {mutation.isLoading ? t('scanner:accountNew.footer.connecting') : t('scanner:accountNew.footer.authenticate')}
            </Button>
          )}
        </Box>
      </Box>

      {/* ── Docs Drawer ─────────────────────────────────────────────────── */}
      <DocsDrawer
        open={docsOpen}
        onClose={() => setDocsOpen(false)}
        doc={DOCS[provider]}
        tipLabel={t('scanner:accountNew.docsDrawer.tip')}
        integrationGuideLabel={t('scanner:accountNew.docsDrawer.integrationGuide')}
      />
    </Box>
  );
};
